import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { AutoSelectionForm } from '@/components/autoforms/AutoSelectionForm';
import { getSDK } from '@/store/sdkStore';
import { dataService } from '@/lib/dataService';
import { useToastStore } from '@/store/toastStore';
import type { ListFilter } from '@/types/formProperties';
import { translate } from '@/lib/i18n';

// Оффлайн-резолв представления ссылки ИЗ КЭША ЧТЕНИЯ (localCache — persisted данные
// сервера, SSoT): когда сервер недоступен, имя находится в кэшированной таблице
// источника (name → code → number, как в 1С). Это НЕ фоллбэк — это оффлайн-чтение.
function findCachedPresentation(types: string[], id: string): string | null {
  for (const t of types || []) {
    const parsed = parseTypeDescriptor(t);
    if (!parsed) continue;
    const rows = dataService.list<Record<string, unknown>>('public', parsed.objectName) || [];
    const found = rows.find((r: any) => String(r.id) === String(id));
    if (found) {
      const v = String(found.name ?? found.code ?? found.number ?? '');
      if (v && v !== 'undefined') return v;
    }
  }
  return null;
}

/** Определяет тип объекта метаданных по коду таблицы (справочник/документ/пользователь).
 *  Экспортируется: FormPreview (предпросмотр форм — пикер ссылок).
 *  ДЕТЕРМИНИЗМ: таблица НЕ в справочниках/документах и не entities → null (ошибка
 *  вызывающего, НЕ фоллбэк); ошибки RPC пробрасываются (не глотаются). */
export async function detectObjectType(table: string): Promise<'catalog' | 'document' | 'entity' | 'task' | 'order' | null> {
  if (table.toLowerCase() === 'entities') return 'entity';
  if (table.toLowerCase() === 'tasks') return 'task';
  if (table.toLowerCase() === 'orders') return 'order';
  const sdk = getSDK();
  const cats = await sdk.metadata.listCatalogs({});
  if ((cats.items || []).some((c: any) => c.code === table)) return 'catalog';
  const docs = await sdk.metadata.listDocuments({});
  if ((docs.items || []).some((d: any) => d.code === table)) return 'document';
  return null;
}

/** Парсит дескриптор типа ('Справочник.X','Документ.Y','entity.entities',
    'task.tasks','order.orders' — встроенные типы, СистемныйТип: М) */
export function parseTypeDescriptor(descriptor: string): { objectType: 'catalog' | 'document' | 'enumeration' | 'entity' | 'task' | 'order'; objectName: string } | null {
  const d = descriptor.trim();
  const lower = d.toLowerCase();
  const parts = d.split('.');
  // СИСТЕМНЫЕ ссылочные типы (как в 1С, СистемныйТип: М): «Любая задача» /
  // «Любая заявка» — резолв по ВСЕМ типам (objectName '*' — выбор типа при пикере);
  // «Любой пользователь» — entities
  if (lower.startsWith('любаязадача') || lower === 'task.any' || lower === 'задача.any') return { objectType: 'task', objectName: '*' };
  if (lower.startsWith('любаязаявка') || lower === 'order.any' || lower === 'заявка.any') return { objectType: 'order', objectName: '*' };
  if (lower.startsWith('любойпользователь') || lower === 'user.any') return { objectType: 'entity', objectName: 'entities' };
  if (parts.length < 2) return null;
  const name = parts.slice(1).join('.');
  if (lower.startsWith('справочник') || lower.startsWith('catalog')) return { objectType: 'catalog', objectName: name };
  if (lower.startsWith('документ') || lower.startsWith('document')) return { objectType: 'document', objectName: name };
  if (lower.startsWith('перечисление') || lower.startsWith('enumeration')) return { objectType: 'enumeration', objectName: name };
  if (lower.startsWith('пользователь') || lower.startsWith('entity')) return { objectType: 'entity', objectName: 'entities' };
  if (lower.startsWith('задача') || lower.startsWith('task')) return { objectType: 'task', objectName: 'tasks' };
  if (lower.startsWith('заявка') || lower.startsWith('order')) return { objectType: 'order', objectName: 'orders' };
  return null;
}

interface ReferencePickerFieldProps {
  value: unknown;
  /** Код справочника/документа-источника (колонка = id этой таблицы) */
  refTable?: string;
  /** Тип объекта-источника (необязательно — определяется автоматически) */
  refType?: 'catalog' | 'document';
  /** Составной тип (как в 1С): дескрипторы — 'Справочник.X','Документ.Y',...
      ОДИН дескриптор = не составной (выбор типа не показывается). */
  types?: string[];
  /** Представление ссылки от СЕРВЕРА ({col}_presentation) — единственный источник отображения */
  displayValue?: string;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Без Label (для ТЧ) */
  compact?: boolean;
  /** Режим выбора (как в 1С): '' (любое) | 'items' (элементы) | 'groups' (группы) | 'both' (любое) */
  selectionMode?: string;
  /** Параметры выбора (как в 1С, уровень метаданных): статичные отборы формы выбора */
  selectionParams?: ListFilter[];
  /** Связи параметров выбора (как в 1С, уровень метаданных): параметр формы выбора
      (например 'Владелец') ← реквизит формы/колонка той же строки ТЧ */
  selectionLinks?: Array<{ param: string; field: string }>;
  /** Значения реквизитов формы / колонок строки ТЧ — источник для связей параметров выбора */
  contextValues?: Record<string, unknown>;
  /** Колонки формы выбора (whitelist): например у валюты — код/название/символ
      (без дат создания/изменения и служебных реквизитов). Пусто = авто-набор. */
  pickColumns?: string[];
}

/** Форматирование дескриптора типа для списка («Справочник: Номенклатура»,
    «Пользователь»); при наличии карты синонимов (code → synonym) подставляется синоним. */
export function formatTypeDescriptor(descriptor: string, synonyms?: Record<string, string>): string {
  const parsed = parseTypeDescriptor(descriptor);
  if (parsed) {
    if (parsed.objectType === 'entity') return 'Пользователь';
    if (parsed.objectType === 'task') return parsed.objectName === '*' ? 'Любая задача' : 'Задача';
    if (parsed.objectType === 'order') return parsed.objectName === '*' ? 'Любая заявка' : 'Заявка';
    const kind = parsed.objectType === 'catalog' ? 'Справочник' : parsed.objectType === 'document' ? 'Документ' : 'Перечисление';
    const name = synonyms?.[parsed.objectName.toLowerCase()] || parsed.objectName;
    return `${kind}: ${name}`;
  }
  return descriptor;
}

/**
 * Ссылочное поле: представление (имя) + "..." (выбор) + "✕" (очистить).
 * ОТОБРАЖЕНИЕ ТОЛЬКО из displayValue (серверный {col}_presentation) или выбранного значения.
 * Составной тип (НЕСКОЛЬКО дескрипторов, как в 1С): при нажатии «...» СНАЧАЛА
 * выбирается тип, затем значение выбранного типа. Выбранный тип фиксируется и
 * запрашивается снова только если поле пустое или было очищено «✕».
 * ОДИН дескриптор = простой тип: сразу форма выбора, без выбора типа.
 * Тип уже заполненного значения определяется СЕРВЕРОМ (ResolveReference) — SSoT.
 * Никакого клиентского резолва UUID и никаких фоллбэков на UUID.
 */
export function ReferencePickerField({ value, refTable, refType, types, displayValue, onChange, disabled, placeholder, compact, selectionMode = '', selectionParams, selectionLinks, contextValues, pickColumns }: ReferencePickerFieldProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPhase, setPickerPhase] = useState<'type' | 'value'>('value');
  const [pickerType, setPickerType] = useState<'catalog' | 'document' | 'entity' | 'task' | 'order'>('catalog');
  const [typeResolved, setTypeResolved] = useState(false);
  // Составной тип: зафиксированный дескриптор (выбран при открытии пикера;
  // сбрасывается только очисткой «✕» или пустым значением)
  const [compositeIndex, setCompositeIndex] = useState<number>(-1);
  // «Любая задача/заявка» (objectName '*'): выбранный КОНКРЕТНЫЙ тип при пикере
  // (список типов задач/заявок); сбрасывается при открытии/очистке
  const [anyTable, setAnyTable] = useState('');
  // Список конкретных типов для «Любая задача/заявка» (SSoT — сервер)
  const [anyTypeChoices, setAnyTypeChoices] = useState<Array<{ code: string; name: string }>>([]);
  // Имя, выбранное в текущей сессии (пока серверное представление не обновилось)
  const [pickedDisplay, setPickedDisplay] = useState('');
  // Синонимы объектов (code → synonym) — для списка типов («Справочник: Сборщики»)
  const [synonyms, setSynonyms] = useState<Record<string, string>>({});

  // Составной тип = БОЛЕЕ ОДНОГО дескриптора; один дескриптор — простой тип (как в 1С)
  const isComposite = !!types && types.length > 1;
  const singleDescriptor = !isComposite && !!types && types.length === 1 ? types[0] : null;
  const activeDescriptor = singleDescriptor || (isComposite && compositeIndex >= 0 ? types[compositeIndex] : null);
  const activeTable = anyTable || refTable || (activeDescriptor ? parseTypeDescriptor(activeDescriptor)?.objectName || '' : '');

  // Отображение: выбранное имя → серверное представление {col}_presentation → «—».
  const hasValue = value !== undefined && value !== null && value !== '';
  const display = pickedDisplay || (displayValue ?? '');

  // Универсальный резолв представления заполненного составного значения
  // (например, предзаполненный владелец при создании из вкладки подчинённых):
  // значение задано, а серверного {col}_presentation ещё нет — определяем через
  // ResolveReference (SSoT); оффлайн — из кэша чтения (localCache).
  // БЕЗ отмены: types — новая ссылка на каждый рендер, отмена «съедала» ответ.
  useEffect(() => {
    if (!hasValue || display || !types || types.length === 0) return;
    const currentValue = String(value);
    getSDK().metadata.resolveReference({ types, id: currentValue })
      .then((r: any) => {
        if (r.presentation) {
          setPickedDisplay((prev) => prev || r.presentation);
        }
      })
      .catch(() => {
        // Оффлайн: имя из кэша чтения (SSoT сервера, закэшировано ранее)
        const cached = findCachedPresentation(types, currentValue);
        if (cached) setPickedDisplay((prev) => prev || cached);
      });
  }, [hasValue, display, types, value]);

  // Загрузка синонимов объектов (SSoT: сервер) — для списка типов составной ссылки:
  // «Справочник: Сборщики» вместо «Справочник: sborshchiki».
  useEffect(() => {
    if (!pickerOpen || pickerPhase !== 'type' || !isComposite) return;
    let cancelled = false;
    (async () => {
      try {
        const md = getSDK().metadata;
        const [cats, docs] = await Promise.all([
          md.listCatalogs({}).catch(() => ({ items: [] })),
          md.listDocuments({}).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const c of (cats as any).items || []) {
          const code = c.code || c.internal_table_name || c.name || '';
          if (code) map[code] = c.synonym || c.name || code;
        }
        for (const d of (docs as any).items || []) {
          const code = d.code || d.internal_table_name || d.name || '';
          if (code) map[code] = d.synonym || d.name || code;
        }
        setSynonyms(map);
        // «Любая задача/заявка»: загружаем конкретные типы (каждый тип — своя таблица)
        const anyDesc = (types || []).find((t) => {
          const p = parseTypeDescriptor(t);
          return p && p.objectName === '*';
        });
        if (anyDesc) {
          const p = parseTypeDescriptor(anyDesc)!;
          // Типы задач/заявок — отдельные сервисы (TaskService/OrderService)
          const resp = p.objectType === 'task'
            ? await getSDK().task.listTaskTypes({}).catch(() => ({ items: [] }))
            : await getSDK().order.listOrderTypes({}).catch(() => ({ items: [] }));
          setAnyTypeChoices(((resp as any)?.items || []).map((t: any) => ({
            code: String(t.code || ''), name: String(t.synonym || t.name || t.code || ''),
          })));
        } else {
          setAnyTypeChoices([]);
        }
      } catch (e: any) {
        // ДЕТЕРМИНИЗМ: список типов недоступен — явная ошибка (не «показываем коды»)
        useToastStore.getState().showError(e?.message || 'Не удалось загрузить список объектов для выбора типа');
        setPickerOpen(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pickerOpen, pickerPhase, isComposite, types]);

  // Определяем тип объекта-источника (для динамического списка формы выбора)
  useEffect(() => {
    if (!pickerOpen || pickerPhase !== 'value' || typeResolved || !activeTable) return;
    let cancelled = false;
    if (refType) {
      setPickerType(refType);
      setTypeResolved(true);
      return;
    }
    detectObjectType(activeTable).then((t) => {
      if (cancelled) return;
      // ДЕТЕРМИНИЗМ: таблица не определена — явная ошибка (не фоллбэк на 'catalog')
      if (!t) {
        useToastStore.getState().showError(`Не удалось определить тип объекта «${activeTable}» (нет в справочниках/документах)`);
        setPickerOpen(false);
        return;
      }
      setPickerType(t);
      setTypeResolved(true);
    }).catch((e: any) => {
      if (cancelled) return;
      useToastStore.getState().showError(e?.message || `Ошибка определения типа объекта «${activeTable}»`);
      setPickerOpen(false);
    });
    return () => { cancelled = true; };
  }, [pickerOpen, pickerPhase, typeResolved, refTable, refType, activeTable]);

  // Определение типа заполненного составного значения — СЕРВЕР (SSoT):
  // id существует только в одной таблице состава.
  const resolveTypeForValue = async (): Promise<number> => {
    try {
      const resp = await getSDK().metadata.resolveReference({ types: types || [], id: String(value) });
      if (!resp.objectName) return -1;
      return (types || []).findIndex((t) => parseTypeDescriptor(t)?.objectName.toLowerCase() === resp.objectName.toLowerCase());
    } catch {
      return -1;
    }
  };

  const openPicker = async () => {
    setTypeResolved(false);
    setPickerOpen(true);
    if (!isComposite) {
      setPickerPhase('value');
      return;
    }
    // Составной тип: ПУСТОЕ поле → ВСЕГДА выбор типа (тип не зафиксирован,
    // даже если ранее выбирался, но значение так и не выбрано).
    // Заполненное поле → тип уже известен (зафиксирован или определяется на сервере).
    if (!hasValue) {
      setPickerPhase('type');
      return;
    }
    if (compositeIndex >= 0) {
      setPickerPhase('value');
    } else {
      const idx = await resolveTypeForValue();
      if (idx >= 0) {
        setCompositeIndex(idx);
        setPickerPhase('value');
      } else {
        setPickerPhase('type');
      }
    }
  };

  // Отборы формы выбора (как в 1С):
  // 1) параметры выбора — статичные условия из метаданных;
  // 2) связи параметров выбора — параметр «Владелец» ← реквизит формы/колонка строки ТЧ:
  //    фильтр owner_id = текущее значение связанного реквизита (подчинённый справочник).
  //    Если значение реквизита пусто — список пуст (нельзя выбрать подчинённого «ничьей» записи).
  const buildSelectionFilters = (): ListFilter[] => {
    const filters: ListFilter[] = [...(selectionParams || [])];
    for (const link of selectionLinks || []) {
      if (link.param !== 'Владелец' || !link.field) continue;
      const v = contextValues?.[link.field];
      if (v === undefined || v === null || v === '') {
        filters.push({ field: 'owner_id', condition: '=', value: '__none__' });
        continue;
      }
      filters.push({ field: 'owner_id', condition: '=', value: String(v) });
    }
    return filters;
  };

  // Данные для СОЗДАНИЯ через форму выбора (как в 1С): новая запись создаётся
  // с предзаполненным владельцем из связи параметров выбора («Владелец» ← реквизит).
  const buildCreateData = (): Record<string, unknown> | undefined => {
    let data: Record<string, unknown> | undefined;
    for (const link of selectionLinks || []) {
      if (link.param !== 'Владелец' || !link.field) continue;
      const v = contextValues?.[link.field];
      if (v === undefined || v === null || v === '') continue;
      data = { ...(data || {}), owner_id: String(v) };
    }
    return data;
  };

  const handlePick = useCallback((selectedId: string, selectedDisplay: string) => {
    onChange(selectedId);
    setPickedDisplay(selectedDisplay);
    setPickerOpen(false);
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setPickedDisplay('');
    setCompositeIndex(-1);
  }, [onChange]);

  const closePicker = useCallback(() => setPickerOpen(false), []);

  const input = (
    <div className={`flex gap-1 w-full ${compact ? '' : ''}`}>
      <Input
        value={display}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? 'Выберите...'}
        disabled={disabled}
        className="h-8 text-sm flex-1 min-w-0"
        readOnly
      />
      {!disabled && (activeTable || isComposite) && (
        <Button
          size="sm" variant="outline" className="h-8 px-2 shrink-0"
          onClick={openPicker}
          title={translate('form.choose_value')}
        >
          ...
        </Button>
      )}
      {!disabled && hasValue && (
        <Button
          size="sm" variant="ghost" className="h-8 px-1.5 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={(e) => { e.stopPropagation(); handleClear(); }}
          title={translate('common.clear')}
        >
          ✕
        </Button>
      )}
    </div>
  );

  return (
    <>
      {compact ? input : <div className="space-y-1">{input}</div>}

      {/* Составной тип: СНАЧАЛА выбор типа (поле пустое/очищено), затем значение */}
      {pickerOpen && pickerPhase === 'type' && isComposite && (
        <UniversalWindow open onClose={closePicker} title={translate('form.choose_type_title')} blocking width="max-w-sm">
          <div className="p-2 space-y-1">
            <div className="text-xs text-muted-foreground px-1 pb-1">{translate('form.choose_type_hint')}</div>
            {types!.map((t, i) => {
              const parsed = parseTypeDescriptor(t);
              // «Любая задача/заявка»: сначала КОНКРЕТНЫЙ тип (каждый тип — своя таблица)
              if (parsed && parsed.objectName === '*') {
                return (
                  <div key={t} className="border rounded px-2 py-1 space-y-0.5">
                    <div className="text-[10px] text-muted-foreground px-1 pt-0.5">{formatTypeDescriptor(t, synonyms)}:</div>
                    {anyTypeChoices.length === 0 && <div className="text-[10px] text-muted-foreground px-1">{translate('form.no_types')}</div>}
                    {anyTypeChoices.map((tc) => (
                      <button
                        key={tc.code}
                        className="w-full text-left text-xs border rounded px-2 py-1 hover:bg-muted/50"
                        onClick={() => {
                          setAnyTable(tc.code);
                          setPickerType(parsed.objectType === 'order' ? 'order' : 'task');
                          setTypeResolved(true);
                          setPickerPhase('value');
                        }}
                      >
                        {tc.name || tc.code}
                      </button>
                    ))}
                  </div>
                );
              }
              return (
                <button
                  key={t}
                  className="w-full text-left text-xs border rounded px-2 py-1.5 hover:bg-muted/50"
                  onClick={() => { setAnyTable(''); setCompositeIndex(i); setPickerPhase('value'); }}
                >
                  {formatTypeDescriptor(t, synonyms)}
                </button>
              );
            })}
          </div>
        </UniversalWindow>
      )}

      {/* Выбор значения выбранного типа */}
      {pickerOpen && pickerPhase === 'value' && activeTable && (
        <UniversalWindow open onClose={closePicker} title={translate('form.choose_value_title')} blocking width="max-w-2xl">
          <AutoSelectionForm
            objectType={pickerType}
            objectName={activeTable}
            onSelect={handlePick}
            selectionMode={selectionMode}
            filters={buildSelectionFilters()}
            createInitialData={buildCreateData()}
            visibleKeys={pickColumns}
          />
        </UniversalWindow>
      )}
    </>
  );
}
